import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /** @type {number | null} */
  #openTimeoutId = null;

  /** @type {number | null} */
  #closeTimeoutId = null;

  /** @type {HTMLElement | null} */
  #pendingOpenItem = null;

  /** @type {{ x: number, y: number }} */
  #pointerPosition = { x: -1, y: -1 };

  /** Delay before opening a submenu on hover to avoid flashing while moving across items */
  #OPEN_DELAY = 150;

  /** Delay before closing a submenu to allow moving into the dropdown panel */
  #CLOSE_DELAY = 250;

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
    this.addEventListener('pointerenter', this.#cancelClose);
    document.addEventListener('pointermove', this.#handlePointerMove, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.removeEventListener('pointerenter', this.#cancelClose);
    document.removeEventListener('pointermove', this.#handlePointerMove);
    this.#cancelOpen();
    this.#cancelClose();
    this.#cleanupMutationObserver();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);


  #overflowSubmenuListener = () => {
    this.#scheduleClose();
  };

  #handlePointerMove = (event) => {
    this.#pointerPosition = { x: event.clientX, y: event.clientY };
  };

  /**
   * @param {Element | null | undefined} element
   * @returns {boolean}
   */
  #isPointerOverElement(element) {
    if (!(element instanceof Element)) return false;

    if (element.matches(':hover')) return true;

    const { x, y } = this.#pointerPosition;
    if (x < 0) return false;

    const topElement = document.elementFromPoint(x, y);
    if (!topElement) return false;

    return element === topElement || element.contains(topElement);
  }

  /**
   * @param {HTMLElement} item
   * @returns {boolean}
   */
  #isPointerOverMenuItem(item) {
    const listItem = item.closest('.menu-list__list-item');
    if (listItem instanceof HTMLElement && this.#isPointerOverElement(listItem)) return true;

    const submenu = findSubmenu(item);
    return submenu ? this.#isPointerOverElement(submenu) : false;
  }

  #cancelOpen = () => {
    if (this.#openTimeoutId !== null) {
      clearTimeout(this.#openTimeoutId);
      this.#openTimeoutId = null;
    }

    this.#pendingOpenItem = null;
  };

  #cancelClose = () => {
    if (this.#closeTimeoutId !== null) {
      clearTimeout(this.#closeTimeoutId);
      this.#closeTimeoutId = null;
    }
  };

  #scheduleClose = () => {
    this.#cancelClose();
    this.#closeTimeoutId = setTimeout(() => {
      this.#closeTimeoutId = null;

      if (this.#shouldKeepOpen()) return;

      this.#deactivate();
    }, this.#CLOSE_DELAY);
  };

  /**
   * @returns {boolean}
   */
  #shouldKeepOpen() {
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return true;

    const activeItem = this.#state.activeItem;
    if (!activeItem) return false;

    const listItem = activeItem.closest('.menu-list__list-item');
    if (listItem instanceof HTMLElement && listItem.matches(':hover')) return true;

    if (activeItem.matches(':hover')) return true;

    const submenu = findSubmenu(activeItem);
    return submenu?.matches(':hover') ?? false;
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.dispatchEvent(new MegaMenuHoverEvent());
    this.#cancelClose();

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    const item = findMenuItem(event.target);
    if (!item || item == this.#state.activeItem) return;

    if (event.type === 'focus' || event.type === 'focusin') {
      this.#cancelOpen();
      this.#activateImmediate(item, event);
      return;
    }

    this.#cancelOpen();
    this.#pendingOpenItem = item;
    this.#openTimeoutId = setTimeout(() => {
      this.#openTimeoutId = null;
      this.#pendingOpenItem = null;

      if (!this.#isPointerOverMenuItem(item)) return;

      this.#activateImmediate(item, event);
    }, this.#OPEN_DELAY);
  };

  /**
   * Open the selected menu item immediately
   * @param {HTMLElement} item
   * @param {PointerEvent | FocusEvent} event
   */
  #activateImmediate(item, event) {
    if (item == this.#state.activeItem) return;

    const listItem = item.closest('.menu-list__list-item');
    const isDefaultSlot = listItem instanceof HTMLElement && listItem.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, {childList: true, subtree: true});

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    if (event.type === 'pointerleave') {
      const relatedTarget = event.relatedTarget instanceof Node ? event.relatedTarget : null;
      const listItem =
        event.target instanceof Element ? event.target.closest('.menu-list__list-item') : null;

      if (listItem instanceof HTMLElement && relatedTarget instanceof Node && listItem.contains(relatedTarget)) {
        return;
      }

      const menuItem = findMenuItem(event.target instanceof Element ? event.target : null);
      const submenu = findSubmenu(menuItem);

      if (relatedTarget instanceof Node && submenu?.contains(relatedTarget)) {
        return;
      }

      const pendingItem = this.#pendingOpenItem;
      if (pendingItem && this.#isPointerOverMenuItem(pendingItem)) {
        return;
      }

      this.#cancelOpen();
    }

    const relatedTarget = event.relatedTarget instanceof Node ? event.relatedTarget : null;
    const menu = findSubmenu(this.#state.activeItem);

    if (relatedTarget) {
      if (menu?.contains(relatedTarget)) return;

      if (this.overflowMenu?.contains(relatedTarget)) return;

      if (relatedTarget instanceof Element && relatedTarget.closest('[slot="overflow"]')) return;

      if (relatedTarget instanceof Element && relatedTarget.closest('.menu-list__list-item') && this.contains(relatedTarget)) {
        this.#scheduleClose();
        return;
      }
    }

    if (event.type === 'blur') {
      const isMovingWithinMenu = relatedTarget instanceof Node && menu?.contains(document.activeElement);
      const isMovingToSubmenu = relatedTarget instanceof Node && menu?.contains(relatedTarget);
      const isMovingToOverflowMenu =
        relatedTarget instanceof Node && relatedTarget.parentElement?.matches('[slot="overflow"]');

      if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) return;
    }

    this.#scheduleClose();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    }

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  if (element.matches('[ref="menuitem"]')) {
    return element;
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
